import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Settle delay (ms) after a preview iframe fires its `load` event before we reveal
 * it. The casino preview is an SPA: `load` only means the shell document arrived,
 * after which it lazily fetches game tiles / art and reflows. Holding the reveal a
 * beat past `load` lets those late assets settle, so the fade-in catches a painted,
 * stable page instead of a half-built one popping into place.
 *
 * It is a guess, and now only a FALLBACK. A framed site that announces itself with
 * {@link RENDERED_MESSAGE} is revealed the moment it says it is done, however long
 * that takes. A site that never announces still reveals here, so the cover cannot hang.
 */
export const IFRAME_SETTLE_MS = 2000;

/**
 * What a framed site posts to its embedder once it has finished rendering:
 * `postMessage({ type: 'page-rendered' }, '*')`. It is the only thing that can tell us
 * the page is actually painted: the preview is cross-origin, so nothing about its
 * layout is observable from this side.
 */
export const RENDERED_MESSAGE = 'page-rendered';

/** The origin a `page-rendered` message must arrive from to be believed, or `null` when
 *  `src` is not a resolvable URL (in which case no message is ever trusted). A `data:`
 *  or `about:` preview yields the opaque `'null'` origin, still an exact-match check. */
export function previewOrigin(src: string | undefined): string | null {
  if (!src) return null;
  try {
    return new URL(src, window.location.href).origin;
  } catch {
    return null;
  }
}

/** Narrow an untrusted `MessageEvent.data` to the rendered announcement. */
export function isRenderedMessage(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  return (data as { type?: unknown }).type === RENDERED_MESSAGE;
}

/**
 * What a framed site posts once it has finished RE-laying-out after a viewport WIDTH
 * change (a Mobile/Desktop switch resizes the morph iframe): `postMessage({ type:
 * 'page-resized' }, '*')`. Distinct from {@link RENDERED_MESSAGE}: that announces the
 * INITIAL paint, this announces "my reflow at the new width has SETTLED". A resize is
 * not a navigation, so the site does not fire `page-rendered` again — and reflow
 * completion is unobservable across origins, so this dedicated message is the only
 * signal that the switch is visually done. Drives the resize cover in MorphDevice. */
export const RESIZED_MESSAGE = 'page-resized';

/** Narrow an untrusted `MessageEvent.data` to the resize-settled announcement. */
export function isResizedMessage(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  return (data as { type?: unknown }).type === RESIZED_MESSAGE;
}

/**
 * Gates the fade-in of a preview iframe. `revealed` flips true on whichever comes first:
 *
 * - the framed site posting `{ type: 'page-rendered' }`, which is authoritative: it is
 *   the only party that knows when it has painted;
 * - the iframe's own `load` plus {@link IFRAME_SETTLE_MS}, the fallback for a site that
 *   does not announce itself.
 *
 * Wire `onLoad` and `iframeRef` to the iframe and drive its `opacity` from `revealed`.
 * Pass the iframe `src`: it fixes the origin a message must come from. Pass `resetKey`
 * only when something other than `src` should re-gate the fade (a reopen, a remount);
 * it defaults to `src`, so a new source re-hides the iframe until its own load.
 *
 * `iframeRef` is not bookkeeping. Several previews share one `src` (the stage keeps a mac
 * and a phone iframe warm on the same URL), so the origin alone cannot say WHICH iframe
 * announced itself. Matching `event.source` against this iframe's own `contentWindow` is
 * what stops the phone's message from lifting the mac's cover, and vice versa.
 */
export function useIframeReveal(
  src: string,
  resetKey: unknown = src,
): { revealed: boolean; onLoad: () => void; iframeRef: RefObject<HTMLIFrameElement> } {
  const [revealed, setRevealed] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // A new source must hide the iframe again until its own load + settle window.
    setRevealed(false);
    return () => window.clearTimeout(timer.current);
  }, [resetKey]);

  useEffect(() => {
    const origin = previewOrigin(src);
    if (origin === null) return undefined;
    const onMessage = (event: MessageEvent<unknown>): void => {
      // Anyone on the page can postMessage us. Believe only this iframe's own window,
      // on the origin we pointed it at, saying the one thing we asked it to say.
      if (event.origin !== origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isRenderedMessage(event.data)) return;
      window.clearTimeout(timer.current);
      setRevealed(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [src]);

  const onLoad = (): void => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setRevealed(true), IFRAME_SETTLE_MS);
  };

  return { revealed, onLoad, iframeRef };
}
